class ByobCollection extends HTMLElement {
  connectedCallback() {
    this.boxQuantity = parseInt(this.dataset.boxQuantity, 10) || 4;
    this.bundlePrice = this.dataset.bundlePrice || '999';
    this.selectedProducts = new Map();
    this.isBuying = false;
    this.storageKey = `byob-selection-${this.dataset.sectionId}`;

    this.counterEl = this.querySelector('[data-byob-count]');
    this.slots = this.querySelectorAll('[data-byob-slot]');
    this.stickyEl = this.querySelector('[data-byob-sticky]');
    this.stickySlots = this.querySelectorAll('[data-byob-sticky-slot]');
    this.stickyLabel = this.querySelector('[data-byob-sticky-label]');
    this.stickySubtext = this.querySelector('[data-byob-sticky-subtext]');
    this.buyBtn = this.querySelector('[data-byob-buy]');
    this.buyBtnText = this.querySelector('[data-byob-buy-text]');
    this.trackerEl = this.querySelector('[data-byob-tracker]');
    this.progressFill = this.querySelector('[data-byob-progress-fill]');
    this.progressBarFill = this.querySelector('[data-byob-progress-bar-fill]');

    this.isInfiniteLoading = false;
    this.infiniteObserver = null;

    this.addEventListener('click', this.handleClick);
    this.restoreSelection();
    this.bindInfiniteScroll();

    // window.addEventListener('pageshow', (event) => {
    //   if (event.persisted) {
    //     try {
    //       const checkoutFlag = sessionStorage.getItem(`${this.storageKey}-checkout`);
    //       if (checkoutFlag === 'true') {
    //         sessionStorage.removeItem(`${this.storageKey}-checkout`);
    //         location.reload();
    //       }
    //     } catch { /* ignore */ }
    //   }
    // });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.infiniteObserver?.disconnect();
  }

  bindInfiniteScroll() {
    if (this.infiniteObserver) this.infiniteObserver.disconnect();

    const sentinel = this.querySelector('[data-byob-sentinel]');
    if (!sentinel?.dataset.nextUrl) return;

    this.infiniteObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || this.isInfiniteLoading) continue;
          const { nextUrl } = sentinel.dataset;
          if (!nextUrl) continue;
          this.appendProducts(nextUrl);
        }
      },
      { rootMargin: '200px' }
    );

    this.infiniteObserver.observe(sentinel);
  }

  async appendProducts(nextUrl) {
    this.isInfiniteLoading = true;

    const sentinel = this.querySelector('[data-byob-sentinel]');
    sentinel?.classList.add('is-loading');

    const sectionId = this.dataset.sectionId;
    const fetchUrl = new URL(nextUrl, window.location.origin);
    fetchUrl.searchParams.set('sections', sectionId);

    try {
      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error('Failed to fetch next page');

      const data = await response.json();
      const html = data[sectionId];
      if (!html) throw new Error('No section HTML returned');

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newGrid = doc.querySelector('[data-byob-grid]');
      const currentGrid = this.querySelector('[data-byob-grid]');
      const newSentinel = doc.querySelector('[data-byob-sentinel]');

      if (newGrid && currentGrid) {
        for (const card of [...newGrid.children]) {
          currentGrid.appendChild(card.cloneNode(true));
        }
        this.updateCards();
      }

      if (sentinel) {
        const nextNextUrl = newSentinel?.dataset.nextUrl;
        if (nextNextUrl) {
          sentinel.dataset.nextUrl = nextNextUrl;
        } else {
          delete sentinel.dataset.nextUrl;
          this.infiniteObserver?.disconnect();
        }
      }
    } catch (error) {
      console.error('[BYOB] Infinite scroll fetch failed:', error);
    } finally {
      sentinel?.classList.remove('is-loading');
      this.isInfiniteLoading = false;
    }
  }

  handleClick = (event) => {
    const removeBtn = event.target.closest('[data-byob-slot-remove]');
    if (removeBtn) {
      event.preventDefault();
      const slot = removeBtn.closest('[data-byob-slot]');
      if (slot) this.handleSlotRemove(slot);
      return;
    }

    const stickyRemoveBtn = event.target.closest('[data-byob-sticky-remove]');
    if (stickyRemoveBtn) {
      event.preventDefault();
      const thumb = stickyRemoveBtn.closest('[data-byob-sticky-slot]');
      if (thumb) {
        const slotIndex = parseInt(thumb.dataset.byobStickySlot, 10) - 1;
        const entries = [...this.selectedProducts.keys()];
        const productId = entries[slotIndex];
        if (productId) {
          this.selectedProducts.delete(productId);
          this.updateUI();
        }
      }
      return;
    }

    const addBtn = event.target.closest('[data-byob-add]');
    if (addBtn) {
      event.preventDefault();
      const card = addBtn.closest('[data-byob-product-id]');
      if (card) this.handleAddToBox(card);
      return;
    }

    const buyBtn = event.target.closest('[data-byob-buy]');
    if (buyBtn) {
      event.preventDefault();
      this.handleBuyNow();
    }
  };

  saveSelection() {
    try {
      const data = [...this.selectedProducts.entries()].map(([id, val]) => [id, val]);
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch {
      /* sessionStorage unavailable */
    }
  }

  restoreSelection() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) return;

      const data = JSON.parse(raw);
      if (!Array.isArray(data)) return;

      data.forEach(([id, val]) => {
        if (id && val?.variantId) {
          this.selectedProducts.set(String(id), val);
        }
      });

      if (this.selectedProducts.size > 0) {
        this.updateUI();
      }
    } catch {
      /* corrupted data — ignore */
    }
  }

  handleSlotRemove(slotEl) {
    const slotIndex = parseInt(slotEl.dataset.byobSlot, 10) - 1;
    const entries = [...this.selectedProducts.keys()];
    const productId = entries[slotIndex];
    if (productId) {
      this.selectedProducts.delete(productId);
      this.updateUI();
    }
  }

  handleAddToBox(cardEl) {
    const productId = cardEl.dataset.byobProductId;

    if (this.selectedProducts.has(productId)) {
      this.selectedProducts.delete(productId);
    } else {
      if (this.selectedProducts.size >= this.boxQuantity) return;

      this.selectedProducts.set(productId, {
        variantId: cardEl.dataset.byobVariantId,
        title: cardEl.dataset.byobProductTitle,
        imageUrl: cardEl.dataset.byobProductImage,
      });
    }

    this.updateUI();
  }

  updateUI() {
    this.updateCounter();
    this.updateSlots();
    this.updateStickySlots();
    this.updateCards();
    this.updateStickyBar();
    this.saveSelection();
  }

  updateCounter() {
    if (this.counterEl) {
      this.counterEl.textContent = this.selectedProducts.size;
    }
    const pct = (this.selectedProducts.size / this.boxQuantity) * 100;
    if (this.progressFill) {
      this.progressFill.style.setProperty('width', `${pct}%`);
    }
    if (this.progressBarFill) {
      this.progressBarFill.style.setProperty('width', `${pct}%`);
    }
  }

  updateSlots() {
    const entries = [...this.selectedProducts.values()];

    this.slots.forEach((slot, index) => {
      const numberEl = slot.querySelector('.byob-tracker__slot-number');
      const labelEl = slot.querySelector('.byob-tracker__slot-label');
      const imageEl = slot.querySelector('.byob-tracker__slot-image');
      const titleEl = slot.querySelector('.byob-tracker__slot-title');
      const checkEl = slot.querySelector('.byob-tracker__slot-check');
      const removeEl = slot.querySelector('[data-byob-slot-remove]');

      slot.classList.remove(
        'byob-tracker__slot--empty',
        'byob-tracker__slot--active',
        'byob-tracker__slot--filled'
      );

      if (index < entries.length) {
        slot.classList.add('byob-tracker__slot--filled');
        if (numberEl) numberEl.hidden = true;
        if (labelEl) labelEl.hidden = true;
        if (imageEl) {
          imageEl.src = entries[index].imageUrl;
          imageEl.alt = entries[index].title;
          imageEl.hidden = false;
        }
        if (titleEl) {
          titleEl.textContent = entries[index].title;
          titleEl.hidden = false;
        }
        if (checkEl) checkEl.hidden = false;
        if (removeEl) removeEl.hidden = false;
      } else if (index === entries.length) {
        slot.classList.add('byob-tracker__slot--active');
        if (numberEl) numberEl.hidden = false;
        if (labelEl) {
          labelEl.textContent = 'PICK NOW';
          labelEl.hidden = false;
        }
        if (imageEl) imageEl.hidden = true;
        if (titleEl) titleEl.hidden = true;
        if (checkEl) checkEl.hidden = true;
        if (removeEl) removeEl.hidden = true;
      } else {
        slot.classList.add('byob-tracker__slot--empty');
        if (numberEl) numberEl.hidden = false;
        if (labelEl) {
          labelEl.textContent = 'EMPTY';
          labelEl.hidden = false;
        }
        if (imageEl) imageEl.hidden = true;
        if (titleEl) titleEl.hidden = true;
        if (checkEl) checkEl.hidden = true;
        if (removeEl) removeEl.hidden = true;
      }
    });
  }

  updateStickySlots() {
    const entries = [...this.selectedProducts.values()];

    this.stickySlots.forEach((thumb, index) => {
      const imageEl = thumb.querySelector('.byob-sticky__thumb-image');
      const placeholderEl = thumb.querySelector('.byob-sticky__thumb-placeholder');
      const removeEl = thumb.querySelector('[data-byob-sticky-remove]');

      if (index < entries.length) {
        thumb.classList.add('byob-sticky__thumb--filled');
        if (imageEl) {
          imageEl.src = entries[index].imageUrl;
          imageEl.alt = entries[index].title;
          imageEl.hidden = false;
        }
        if (placeholderEl) placeholderEl.hidden = true;
        if (removeEl) removeEl.hidden = false;
      } else {
        thumb.classList.remove('byob-sticky__thumb--filled');
        if (imageEl) imageEl.hidden = true;
        if (placeholderEl) placeholderEl.hidden = false;
        if (removeEl) removeEl.hidden = true;
      }
    });
  }

  updateCards() {
    const cards = this.querySelectorAll('[data-byob-product-id]');
    const isFull = this.selectedProducts.size >= this.boxQuantity;

    cards.forEach((card) => {
      const productId = card.dataset.byobProductId;
      const btn = card.querySelector('[data-byob-add]');
      if (!btn) return;

      const isSelected = this.selectedProducts.has(productId);

      if (isSelected) {
        card.classList.add('byob-card--selected');
        btn.classList.add('byob-card__btn--added');
        btn.classList.remove('byob-card__btn--disabled');
      btn.textContent = '✓ ADDED TO BOX';
        btn.disabled = false;
      } else if (isFull) {
        card.classList.remove('byob-card--selected');
        btn.classList.remove('byob-card__btn--added');
        btn.classList.add('byob-card__btn--disabled');
        btn.textContent = 'BUY @ 199';
        btn.disabled = true;
      } else {
        card.classList.remove('byob-card--selected');
        btn.classList.remove('byob-card__btn--added', 'byob-card__btn--disabled');
        btn.textContent = 'BUY @ 199';
        btn.disabled = false;
      }
    });
  }

  updateStickyBar() {
    const count = this.selectedProducts.size;
    const isComplete = count >= this.boxQuantity;

    if (count > 0) {
      this.stickyEl?.classList.remove('byob-sticky--hidden');
      this.stickyEl?.classList.add('byob-sticky--visible');
    } else {
      this.stickyEl?.classList.remove('byob-sticky--visible');
      this.stickyEl?.classList.add('byob-sticky--hidden');
    }

    if (isComplete) {
      this.stickyEl?.classList.add('byob-sticky--complete');
      if (this.stickyLabel) this.stickyLabel.textContent = 'BOX COMPLETE';
      if (this.stickySubtext) {
        this.stickySubtext.innerHTML =
          `<span style="color:#16a34a;font-weight:600">All set!</span> ${this.boxQuantity} products · ₹${this.bundlePrice}`;
      }
      if (this.buyBtn) {
        this.buyBtn.classList.remove('byob-sticky__btn--disabled');
        this.buyBtn.classList.add('byob-sticky__btn--active');
        this.buyBtn.disabled = false;
      }
      if (this.buyBtnText) {
        this.buyBtnText.textContent = `BUY NOW | ₹${this.bundlePrice}`;
      }
    } else {
      this.stickyEl?.classList.remove('byob-sticky--complete');
      if (this.stickyLabel) this.stickyLabel.textContent = 'BUILD YOUR BOX';
      if (this.stickySubtext) {
        const remaining = this.boxQuantity - count;
        this.stickySubtext.innerHTML =
          `<strong>${count}</strong> of ${this.boxQuantity} added · <span style="color:#cd1c18;text-decoration:underline">pick ${remaining} more</span>`;
      }
      if (this.buyBtn) {
        this.buyBtn.classList.add('byob-sticky__btn--disabled');
        this.buyBtn.classList.remove('byob-sticky__btn--active');
        this.buyBtn.disabled = true;
      }
      if (this.buyBtnText) {
        this.buyBtnText.textContent = 'BUY NOW | ₹—';
      }
    }
  }

  async handleBuyNow() {
    if (this.selectedProducts.size < this.boxQuantity || this.isBuying) return;

    this.isBuying = true;
    if (this.buyBtn) {
      this.buyBtn.disabled = true;
      this.buyBtn.classList.add('byob-sticky__btn--loading');
    }

    const items = [...this.selectedProducts.values()].map((product) => ({
      id: parseInt(product.variantId, 10),
      quantity: 1,
    }));

    const root = window.Shopify?.routes?.root || '/';

    try {
      await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

      if (typeof fetchCart === 'function') {
        fetchCart();
      }

      try { sessionStorage.setItem(`${this.storageKey}-checkout`, 'true'); } catch { /* ignore */ }

      if (typeof onCheckoutClick === 'function') {
        onCheckoutClick(this.buyBtn);
        this.isBuying = false;
        if (this.buyBtn) {
          this.buyBtn.disabled = false;
          this.buyBtn.classList.remove('byob-sticky__btn--loading');
        }
      } else {
        window.location.href = `${root}checkout`;
      }
    } catch (error) {
      this.isBuying = false;
      if (this.buyBtn) {
        this.buyBtn.disabled = false;
        this.buyBtn.classList.remove('byob-sticky__btn--loading');
      }
      console.error('[BYOB] Cart add failed:', error);
    }
  }

}

if (!customElements.get('byob-collection')) {
  customElements.define('byob-collection', ByobCollection);
}
